import { useCallback, useEffect } from 'react'
import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  Path,
} from 'react-hook-form'
import { View } from 'react-native'
import { useSharedValue } from 'react-native-reanimated'
import { StyleSheet } from 'react-native-unistyles'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldNumber from '@/components/form/FormFieldNumber'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldMedicationName from '@/components/fosters/cards/formFields/FormFieldMedicationName'
import { useFosterFormContext } from '@/components/fosters/FosterFormContext'
import AccordionItem from '@/components/shared/AccordionItem'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import Species from '@/enums/species'
import UserService from '@/services/userService'

interface Props<C extends FieldValues, _E extends FieldErrors> {
  control: Control<C, any>
  disabled: boolean
  formState: FormState<C>
  index: number
  medication?: any
  open: boolean
  species: Species | undefined
}

const FosterMedicationItemLarge = <
  C extends FieldValues,
  E extends FieldErrors,
>({
  control,
  disabled,
  formState,
  index,
  medication,
  open,
  species,
}: Props<C, E>) => {
  const { setValue } = useFosterFormContext()
  const { user } = UserService.useCurrentUser()
  const isExpanded = useSharedValue(open)

  useEffect(() => {
    isExpanded.value = open
  }, [isExpanded, open])

  const pathPrefix = `medications.${index}`

  const markMedicationAsGiven = useCallback(() => {
    setValue(`${pathPrefix}.givenDate` as any, new Date() as any, {
      shouldDirty: true,
    })
    setValue(
      `${pathPrefix}.givenBy` as any,
      `${user?.firstName || ''} ${user?.lastName || ''}` as any,
      { shouldDirty: true }
    )
  }, [pathPrefix, setValue, user?.firstName, user?.lastName])

  return (
    <AccordionItem isExpanded={isExpanded} viewKey={`wide_medication_${index}`}>
      <View style={styles.content}>
        <View style={styles.nameRow}>
          <FormFieldMedicationName
            control={control}
            disabled={disabled}
            fieldName={`${pathPrefix}.name` as Path<C>}
            formState={formState}
            label="Medication Name"
            required={true}
            species={species as Species}
            style={styles.nameField}
          />
        </View>

        <View style={styles.dateRow}>
          {!medication?.givenDate && (
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={`${pathPrefix}.dueDate` as Path<C>}
              label="Due On"
              required={false}
              style={styles.dateField}
            />
          )}
          <FormFieldDate
            control={control}
            disabled={disabled}
            errors={formState.errors}
            fieldName={`${pathPrefix}.givenDate` as Path<C>}
            label="Given On"
            required={false}
            style={styles.dateField}
          />
          <FormFieldText
            control={control}
            disabled={disabled}
            errors={formState.errors}
            fieldName={`${pathPrefix}.givenBy` as Path<C>}
            label="Given By"
            required={true}
            style={styles.dateField}
          />
        </View>

        {!medication?.givenDate && (
          <UniButton onPress={markMedicationAsGiven} style={styles.giveButton}>
            I just gave this medication
          </UniButton>
        )}

        <FormFieldText
          control={control}
          disabled={disabled}
          errors={formState.errors}
          fieldName={`${pathPrefix}.notes` as Path<C>}
          label="Notes"
          multiline
          numberOfLines={2}
          required={false}
          style={styles.notesField}
        />

        <View style={styles.repeatRow}>
          <FormFieldCheckbox
            control={control}
            disabled={disabled}
            fieldName={`${pathPrefix}.isRepeating` as Path<C>}
            label="Repeat this medication"
            required={false}
            status="unchecked"
          />
          {medication?.isRepeating && (
            <View style={styles.repeatContainer}>
              <UniText variant="bodyMedium">every </UniText>
              <FormFieldNumber
                aria-label="Frequency in days"
                control={control}
                disabled={disabled}
                errors={formState.errors}
                fieldName={`${pathPrefix}.frequency` as Path<C>}
                required={false}
                style={styles.frequency}
              />
              <UniText variant="bodyMedium"> days</UniText>
            </View>
          )}
        </View>
      </View>
    </AccordionItem>
  )
}

export default FosterMedicationItemLarge

const styles = StyleSheet.create((theme) => ({
  content: {
    gap: theme.tokens.spacing[4],
    paddingBottom: theme.tokens.spacing[5],
    paddingHorizontal: theme.tokens.spacing[5],
    paddingTop: theme.tokens.spacing[3],
    width: '100%',
  },
  dateField: {
    flex: 1,
    minWidth: 150,
  },
  dateRow: {
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
  },
  frequency: {
    marginLeft: theme.tokens.spacing[2],
    marginRight: theme.tokens.spacing[2],
    width: 60,
  },
  giveButton: {
    alignSelf: 'flex-start',
  },
  nameField: {
    flex: 1,
    maxWidth: 400,
  },
  nameRow: {
    flexDirection: 'row',
  },
  notesField: {
    flex: 1,
  },
  repeatContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  repeatRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[3],
  },
}))
